/**
 * The battle pass fill on a game's post-match screen (ACT HQ t-935; Theo, 14 Oct 2026: "lets
 * add battlepass progress to the post game screens of all the games"). A game mounts it in its
 * results screen when its server's award arrives (PASS_AWARD_MESSAGE from the room, or Vector
 * Drift's race finish), and it fills from where the player was to where they are now, level by
 * level, with the game's lines and the daily bonus under it.
 *
 * Import-light on purpose (DOM only, its own styles), so Ludus and Vector Drift mount it
 * without the client-core barrel: `import { mountPassFill } from "@act/client-core/passFill"`.
 * It lays out inside the host (width 100%, sized in em from the host's font-size), takes its
 * colours from --xp-fill, --xp-track and --xp-ink on the host or above, and starts when
 * mounted. A tap or click skips to the end, as does skip() (for a pad).
 *
 *   room.onMessage(PASS_AWARD_MESSAGE, (award: PassAwardMessage) => mountPassFill(resultsEl, award));
 */
import { passLevelOf, type PassAwardMessage } from "@act/shared";

/** One level's stretch of the fill: the bar from `from` to `to` (0..1) at `level`, which costs `cost` XP (null at 50). */
export type PassFillSegment = { level: number; cost: number | null; from: number; to: number; levelUp: boolean };

/** The fill's stretches for XP going from `before` to `after` on a season's curve: a level-up
 *  fills the bar to the end, then the next level starts from empty. */
export function passFillSegments(levelXp: readonly number[], before: number, after: number): PassFillSegment[] {
  const out: PassFillSegment[] = [];
  let xp = Math.max(0, before);
  const end = Math.max(xp, after);
  for (let guard = 0; guard <= levelXp.length; guard++) {
    const at = passLevelOf(levelXp, xp);
    if (at.next === null) {
      out.push({ level: at.level, cost: null, from: 1, to: 1, levelUp: false });
      break;
    }
    const start = xp - at.into; // where this level began
    if (end < start + at.next) {
      out.push({ level: at.level, cost: at.next, from: at.into / at.next, to: (end - start) / at.next, levelUp: false });
      break;
    }
    out.push({ level: at.level, cost: at.next, from: at.into / at.next, to: 1, levelUp: true });
    xp = start + at.next;
  }
  return out;
}

export type PassFillOptions = {
  /** Show the end at once. Default: the system's reduce-motion setting. */
  reduceMotion?: boolean;
  /** Each level gained, as the fill passes it (once, for the last, when skipped): for the game's own sound. */
  onLevelUp?: (level: number) => void;
  /** The season's level costs, for an award that came without them (a server before t-935). */
  levelXp?: readonly number[];
};

export type PassFill = {
  readonly el: HTMLElement;
  /** Jump to the end. */
  skip(): void;
  /** Resolves when the fill has finished, been skipped or been removed. */
  readonly done: Promise<void>;
  dispose(): void;
};

const STYLE_ID = "act-pass-fill-style";
const STYLES = `
.act-pass-fill { display: block; width: 100%; box-sizing: border-box; color: var(--xp-ink, #e6edf3); line-height: 1.25; cursor: pointer; user-select: none; -webkit-user-select: none; -webkit-tap-highlight-color: transparent; }
.act-pass-fill * { box-sizing: border-box; }
.act-pass-fill-head { display: flex; align-items: center; gap: 0.5em; margin-bottom: 0.4em; }
.act-pass-fill-icon { display: inline-grid; place-items: center; width: 1.2em; height: 1.2em; color: var(--xp-fill, #8fd6c8); flex: none; }
.act-pass-fill-icon svg { width: 100%; height: 100%; }
.act-pass-fill-title { flex: 0 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; font-size: 0.72em; letter-spacing: 0.12em; text-transform: uppercase; opacity: 0.75; white-space: nowrap; }
.act-pass-fill-level { display: inline-block; flex: none; margin-left: auto; font-weight: 800; letter-spacing: 0.06em; white-space: nowrap; font-variant-numeric: tabular-nums; }
.act-pass-fill-gain { flex: none; font-weight: 800; color: var(--xp-fill, #8fd6c8); white-space: nowrap; font-variant-numeric: tabular-nums; }
.act-pass-fill-bar { position: relative; height: 0.6em; border-radius: 0.3em; background: var(--xp-track, rgba(110, 130, 150, 0.28)); overflow: hidden; }
.act-pass-fill-fill { position: absolute; top: 0; bottom: 0; left: 0; width: 0; border-radius: inherit; background: var(--xp-fill, #8fd6c8); }
.act-pass-fill-xp { margin-top: 0.3em; font-size: 0.72em; text-align: right; opacity: 0.7; font-variant-numeric: tabular-nums; }
.act-pass-fill-lines { display: grid; gap: 0.2em; margin: 0.5em 0 0; padding: 0; list-style: none; font-size: 0.78em; }
.act-pass-fill-lines li { display: flex; justify-content: space-between; gap: 1em; letter-spacing: 0.06em; text-transform: uppercase; }
.act-pass-fill-lines b { color: var(--xp-fill, #8fd6c8); font-variant-numeric: tabular-nums; }
.act-pass-fill-sr { position: absolute; width: 1px; height: 1px; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
.act-pass-fill.up .act-pass-fill-bar { animation: act-pass-fill-flash 0.55s ease-out; }
.act-pass-fill.up .act-pass-fill-level { animation: act-pass-fill-pop 0.55s ease-out; }
@keyframes act-pass-fill-flash { 0% { box-shadow: 0 0 0 0 transparent; } 35% { box-shadow: 0 0 0.9em 0.15em var(--xp-fill, #8fd6c8); } 100% { box-shadow: 0 0 0 0 transparent; } }
@keyframes act-pass-fill-pop { 0% { transform: scale(1); } 35% { transform: scale(1.12); } 100% { transform: scale(1); } }
@media (prefers-reduced-motion: reduce) { .act-pass-fill.up .act-pass-fill-bar, .act-pass-fill.up .act-pass-fill-level { animation: none; } }
`;

const CHEST =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 10h18v10H3z"/><path d="M3 10l2.5-5h13L21 10"/><path d="M10 13h4v3h-4z"/></svg>';

const fmt = (n: number) => Math.round(n).toLocaleString("en-GB");

/** Put the fill into `host` (a results screen) and play it. */
export function mountPassFill(host: HTMLElement, award: PassAwardMessage, opts: PassFillOptions = {}): PassFill {
  const doc = host.ownerDocument;
  if (!doc.getElementById(STYLE_ID)) {
    const style = doc.createElement("style");
    style.id = STYLE_ID;
    style.textContent = STYLES;
    doc.head.appendChild(style);
  }
  const make = (tag: string, className: string, text?: string) => {
    const node = doc.createElement(tag);
    node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const leveled = award.levelAfter > award.levelBefore;
  const summary = `Battle pass: +${fmt(award.xpGained)} XP, level ${award.levelAfter}${leveled ? `, up from ${award.levelBefore}` : ""}`;
  const el = make("div", "act-pass-fill");
  el.setAttribute("role", "group");
  el.setAttribute("aria-label", summary);
  const head = make("div", "act-pass-fill-head");
  head.setAttribute("aria-hidden", "true");
  const icon = make("span", "act-pass-fill-icon");
  icon.innerHTML = CHEST;
  const level = make("span", "act-pass-fill-level", `LV ${award.levelBefore}`);
  head.append(icon, make("span", "act-pass-fill-title", "Battle pass"), level, make("span", "act-pass-fill-gain", `+${fmt(award.xpGained)} XP`));
  const bar = make("div", "act-pass-fill-bar");
  bar.setAttribute("aria-hidden", "true");
  const fill = make("div", "act-pass-fill-fill");
  bar.appendChild(fill);
  const xpText = make("div", "act-pass-fill-xp");
  xpText.setAttribute("aria-hidden", "true");
  el.append(head, bar, xpText);
  const rows = [...award.lines];
  if (award.dailyBonus > 0) rows.push({ label: "Daily bonus", xp: award.dailyBonus });
  if (rows.length) {
    const list = make("ul", "act-pass-fill-lines");
    for (const row of rows) {
      const li = make("li", "");
      li.append(make("span", "", row.label), make("b", "", `${row.xp < 0 ? "−" : "+"}${fmt(Math.abs(row.xp))}`));
      list.appendChild(li);
    }
    el.appendChild(list);
  }
  const live = make("span", "act-pass-fill-sr");
  live.setAttribute("role", "status");
  el.appendChild(live);
  host.appendChild(el);

  const levelXp = award.levelXp ?? opts.levelXp ?? null;
  const segments = levelXp && levelXp.length > 0 ? passFillSegments(levelXp, award.xpAfter - award.xpGained, award.xpAfter) : [];
  if (segments.length === 0) {
    bar.hidden = true;
    xpText.hidden = true;
  }

  let resolveDone!: () => void;
  const done = new Promise<void>((r) => (resolveDone = r));
  let finished = false;
  let raf = 0;
  let signalled = award.levelBefore;

  const show = (seg: PassFillSegment, share: number) => {
    fill.style.width = `${(share * 100).toFixed(2)}%`;
    xpText.textContent = seg.cost === null ? "MAX" : `${fmt(share * seg.cost)} / ${fmt(seg.cost)} XP`;
  };
  const flash = () => {
    el.classList.remove("up");
    void el.offsetWidth; // restart the animation for each level
    el.classList.add("up");
  };
  /** The end state; `quiet` when the screen is going away (no level-up sound then). */
  const finish = (quiet = false) => {
    if (finished) return;
    finished = true;
    if (raf) cancelAnimationFrame(raf);
    const last = segments[segments.length - 1];
    if (last) show(last, last.to);
    level.textContent = leveled ? `LV ${award.levelBefore} → ${award.levelAfter}` : `LV ${award.levelAfter}`;
    if (award.levelAfter > signalled && !quiet) {
      signalled = award.levelAfter;
      flash();
      opts.onLevelUp?.(award.levelAfter);
    }
    live.textContent = summary;
    resolveDone();
  };

  const reduce = opts.reduceMotion ?? (typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches);
  if (reduce || segments.length === 0 || typeof requestAnimationFrame !== "function") {
    finish();
  } else {
    // Each level's stretch gets its share of 0.5-2.6 s by how much of the bar it covers.
    const weights = segments.map((s) => 0.25 + Math.max(0, s.to - s.from));
    const sum = weights.reduce((a, b) => a + b, 0);
    const total = Math.min(2600, Math.max(500, 900 * sum));
    const plan = segments.map((s, i) => ({ ...s, ms: (total * weights[i]!) / sum }));
    let i = 0;
    let t0 = -1;
    show(plan[0]!, plan[0]!.from);
    const step = (now: number) => {
      raf = 0;
      if (finished) return;
      if (!el.isConnected) return finish(true);
      if (t0 < 0) t0 = now;
      const seg = plan[i]!;
      const k = Math.min(1, (now - t0) / seg.ms);
      const eased = 1 - (1 - k) ** 3;
      show(seg, seg.from + (seg.to - seg.from) * eased);
      level.textContent = `LV ${seg.level}`;
      if (k >= 1) {
        if (seg.levelUp) {
          signalled = seg.level + 1;
          flash();
          opts.onLevelUp?.(seg.level + 1);
        }
        i++;
        t0 = now;
        if (i >= plan.length) return finish();
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
  }

  const skip = () => finish();
  el.addEventListener("click", skip);
  return {
    el,
    skip,
    done,
    dispose() {
      finish(true);
      el.removeEventListener("click", skip);
      el.remove();
    },
  };
}
